// Copyright (c) Ping. All rights reserved.
//
// Renders a message-preview string with its leading emoji glyph swapped for a
// lucide icon. `formatPreview.ts` prefixes media/rich previews with an emoji
// ("📷 Photo", "🎤 Voice message", …) — that text form stays authoritative for
// OS notifications, search, and any plain-text surface, but visual tiles (reply
// quotes, the chat-list row) should show a crisp icon instead of the emoji.
//
// A preview with no known leading glyph (plain text, or a sender-prefixed
// "Alice: hi") renders unchanged — so this is a safe drop-in for any preview
// `<Text>`.

import type React from 'react';
import { type StyleProp, Text, type TextStyle, View } from 'react-native';
import {
  BarChart3,
  Calendar,
  Camera,
  Code,
  FileArchive,
  Film,
  Gamepad2,
  type LucideIcon,
  Link2,
  MapPin,
  Mic,
  Paperclip,
  Phone,
  Pin,
  Sticker,
  Video,
} from 'lucide-react-native';

import {
  CALL_AUDIO_GLYPH,
  CALL_VIDEO_GLYPH,
  CODE_GLYPH,
  FILE_GLYPH,
  GIF_GLYPH,
  LINK_GLYPH,
  LOCATION_GLYPH,
  MEETING_GLYPH,
  PHOTO_GLYPH,
  PIN_GLYPH,
  POLL_GLYPH,
  STICKER_GLYPH,
  VIDEO_GLYPH,
  VOICE_GLYPH,
} from './formatPreview';

/** Game preview glyph — formatPreview doesn't export it, so declare it here. */
const GAME_GLYPH = '🎮';

/** Preview glyph → the lucide icon that replaces it. */
const GLYPH_ICON: ReadonlyArray<readonly [string, LucideIcon]> = [
  [PHOTO_GLYPH, Camera],
  [VIDEO_GLYPH, Video],
  [CALL_VIDEO_GLYPH, Video],
  [VOICE_GLYPH, Mic],
  [FILE_GLYPH, Paperclip],
  [LINK_GLYPH, Link2],
  [STICKER_GLYPH, Sticker],
  [GIF_GLYPH, Film],
  [LOCATION_GLYPH, MapPin],
  [CALL_AUDIO_GLYPH, Phone],
  [MEETING_GLYPH, Calendar],
  [POLL_GLYPH, BarChart3],
  [PIN_GLYPH, Pin],
  [GAME_GLYPH, Gamepad2],
  [CODE_GLYPH, Code],
];

/**
 * Splits a known kind-glyph off a preview string and returns its lucide icon
 * plus the cleaned text. The glyph is lifted only when it sits at the very
 * start OR directly after a "<sender>: " group prefix (so a literal emoji typed
 * mid-sentence is never mistaken for a kind marker); the sender label is kept.
 * A `.zip` document uses the archive icon.
 */
export function splitPreviewGlyph(text: string): {
  readonly Icon: LucideIcon | null;
  readonly label: string;
} {
  for (const [glyph, Icon] of GLYPH_ICON) {
    const needle = `${glyph} `;
    const at = text.indexOf(needle);
    if (at === -1) continue;
    const before = text.slice(0, at);
    if (at !== 0 && !before.endsWith(': ')) continue;
    const after = text.slice(at + needle.length);
    const label = `${before}${after}`.trim();
    const ResolvedIcon =
      glyph === FILE_GLYPH && /\.zip(\b|$)/i.test(after) ? FileArchive : Icon;
    return { Icon: ResolvedIcon, label: label.length > 0 ? label : after };
  }
  return { Icon: null, label: text };
}

export interface PreviewLabelProps {
  readonly text: string;
  /** Text + icon color (lucide takes a `color` prop, not a class). */
  readonly color: string;
  /** Icon side length; the text size stays governed by `style` / `className`. */
  readonly iconSize?: number;
  readonly numberOfLines?: number;
  /** StyleSheet-based surfaces (reply quote). */
  readonly style?: StyleProp<TextStyle>;
  /** NativeWind surfaces (pinned bar) — applied to the text node. */
  readonly className?: string;
  readonly testID?: string;
}

/**
 * A preview `<Text>` whose leading emoji glyph is rendered as a lucide icon.
 * Falls back to a bare `<Text>` (no wrapper) when there's no known glyph, so
 * plain-text previews keep their exact prior layout.
 */
export const PreviewLabel: React.FC<PreviewLabelProps> = ({
  text,
  color,
  iconSize = 13,
  numberOfLines,
  style,
  className,
  testID,
}) => {
  const { Icon, label } = splitPreviewGlyph(text);
  if (Icon === null) {
    return (
      <Text
        style={[style, { color }]}
        {...(className !== undefined ? { className } : {})}
        numberOfLines={numberOfLines}
        testID={testID}
      >
        {label}
      </Text>
    );
  }
  // The caller's `style` carries the row's layout intent (flex / margin) as well
  // as typography, so apply it to the outer row too — otherwise a `flex:1`
  // preview would collapse to its content width and shove a trailing badge in.
  return (
    <View
      style={[{ flexDirection: 'row', alignItems: 'center', gap: 4 }, style]}
      {...(className !== undefined ? { className: `flex-row items-center ${className}` } : {})}
      testID={testID}
    >
      <Icon size={iconSize} strokeWidth={2} color={color} />
      <Text
        style={[style, { color, flexShrink: 1 }]}
        {...(className !== undefined ? { className } : {})}
        numberOfLines={numberOfLines}
      >
        {label}
      </Text>
    </View>
  );
};
